/**
 * Utility functions for project-related operations
 */

import { COUNTRY_LABELS } from "../constants/countryAddress";

// ─── Terminology helper ───────────────────────────────────────────────────────
// Projects can use country-specific terminology everywhere the user sees
// "Change Order / Change Orders".
//
// TO REMOVE THIS FEATURE: delete getChangeOrderLabel, then replace all
// getChangeOrderLabel(project, true)  → "Change Orders"
// getChangeOrderLabel(project)        → "Change Order"
// ─────────────────────────────────────────────────────────────────────────────
export const getChangeOrderLabel = (project, plural = false) => {
  const countryCode = (project?.country_code || project?.country || "")
    .toLowerCase()
    .trim();

  if (countryCode === "aus") {
    return plural ? "Variations" : "Variation";
  }
  if (
    countryCode === "nzl" ||
    countryCode === "nz" ||
    countryCode === "new zealand"
  ) {
    return plural ? "Contract Instructions" : "Contract Instruction";
  }
  return plural ? "Change Orders" : "Change Order";
};

/** Label for `country_code` (matches Country / Region options). */
export const getCountryLabel = (code) => {
  if (code == null || code === "") return "";
  return COUNTRY_LABELS[String(code).toLowerCase().trim()] || "";
};

/**
 * Get the most recent company logo from user's existing projects
 * @param {Array} projects - Array of user's projects
 * @returns {Object|null} - The most recent company logo or null if none found
 */
export const getDefaultCompanyLogo = (projects) => {
  if (!projects || !Array.isArray(projects)) {
    return null;
  }

  // Find projects that have company logos
  const projectsWithLogos = projects.filter(project => 
    project.company_logo && 
    Array.isArray(project.company_logo) && 
    project.company_logo.length > 0
  );

  if (projectsWithLogos.length === 0) {
    return null;
  }

  // Sort projects by project_tier (premium projects first) and then by project_id
  // This ensures we get the logo from the most important/recent project
  const sortedProjects = projectsWithLogos.sort((a, b) => {
    // First sort by project_tier (higher tier first)
    if (a.project_tier !== b.project_tier) {
      return (b.project_tier || 0) - (a.project_tier || 0);
    }
    // Then sort by project_id (assuming newer projects have higher IDs)
    return b.project_id.localeCompare(a.project_id);
  });

  // Return the first logo from the most important project
  const mostRecentProject = sortedProjects[0];
  return mostRecentProject.company_logo[0];
};

/**
 * Check if a project has a company logo
 * @param {Object} project - Project object
 * @returns {boolean} - True if project has company logo
 */
export const hasCompanyLogo = (project) => {
  return project && 
         project.company_logo && 
         Array.isArray(project.company_logo) && 
         project.company_logo.length > 0;
};

export const normalizeBooleanValue = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase();
    if (trimmed === "true" || trimmed === "1") return true;
    if (trimmed === "false" || trimmed === "0") return false;
  }

  return false;
};

export const isTimeChangeOrdersEnabled = (project) =>
  normalizeBooleanValue(
    project?.settings?.time_change_orders ?? project?.time_change_orders
  );

export const TIME_CONTRACT_COLUMNS = [
  "duration",
  "approved_change_order_days",
  "revised_duration",
];

export const TIME_CHANGE_ORDER_COLUMNS = ["days"];

export const TIME_COLUMNS_INITIALIZED = "__time_columns_initialized";

const withoutTimeColumnMeta = (columns = []) =>
  columns.filter((column) => column && column !== TIME_COLUMNS_INITIALIZED);

const withTimeColumnMeta = (columns = []) => [
  ...withoutTimeColumnMeta(columns),
  TIME_COLUMNS_INITIALIZED,
];

export const insertColumnsBeforeAction = (columns = [], extraKeys = []) => {
  const existing = new Set(columns);
  const missing = extraKeys.filter((key) => !existing.has(key));
  if (!missing.length) return columns;
  const actionIdx = columns.indexOf("action");
  if (actionIdx === -1) return [...columns, ...missing];
  return [...columns.slice(0, actionIdx), ...missing, ...columns.slice(actionIdx)];
};

export const getTimeAwareColumnPreference = ({
  savedColumns,
  defaultColumns = [],
  timeColumns = [],
}) => {
  const saved = Array.isArray(savedColumns) ? savedColumns : [];
  const initialized = saved.includes(TIME_COLUMNS_INITIALIZED);
  const cleaned = withoutTimeColumnMeta(saved);
  const base = cleaned.length > 0 ? cleaned : defaultColumns;
  if (initialized) {
    return cleaned;
  }
  return insertColumnsBeforeAction(base, timeColumns);
};

export const persistTimeAwareColumns = ({
  selection = [],
  previousPreference = [],
  enabled = false,
  timeColumns = [],
}) => {
  const cleaned = withoutTimeColumnMeta(selection);
  if (enabled) {
    return withTimeColumnMeta(cleaned);
  }
  const preserved = withoutTimeColumnMeta(previousPreference).filter((column) =>
    timeColumns.includes(column),
  );
  return withTimeColumnMeta(
    insertColumnsBeforeAction(
      cleaned.filter((column) => !timeColumns.includes(column)),
      preserved,
    ),
  );
};

export const formatDurationDays = (value) => {
  if (value === null || value === undefined || value === "") return "0";
  const n = Number(value);
  return Number.isNaN(n) ? "0" : String(n);
};

export const MATERIAL_RELEASE_COLUMNS = [
  "released_on",
  "estimated_delivery_date",
];

export const MATERIAL_RELEASE_FILTER_FIELDS = [
  "released_on",
  "estimated_delivery_date",
];

export const getAvailableSubmittalColumns = (columns = [], enabled = false) => {
  if (enabled) {
    return columns;
  }

  return columns.filter(
    (column) => !MATERIAL_RELEASE_COLUMNS.includes(column)
  );
};

export const getDefaultSubmittalColumns = (columns = [], enabled = false) => {
  if (!enabled) {
    return getAvailableSubmittalColumns(columns, enabled);
  }

  return Array.from(new Set([...columns, ...MATERIAL_RELEASE_COLUMNS]));
};

const haveSameColumnSet = (first = [], second = []) => {
  if (first.length !== second.length) {
    return false;
  }

  const secondSet = new Set(second);
  return first.every((column) => secondSet.has(column));
};

export const getInitialSubmittalColumns = ({
  savedColumns = [],
  defaultColumns = [],
  enabled = false,
}) => {
  const normalizedSavedColumns = Array.isArray(savedColumns) ? savedColumns : [];
  const normalizedDefaultColumns = Array.isArray(defaultColumns)
    ? defaultColumns
    : [];

  if (normalizedSavedColumns.length === 0) {
    return getDefaultSubmittalColumns(normalizedDefaultColumns, enabled);
  }

  const availableSavedColumns = getAvailableSubmittalColumns(
    normalizedSavedColumns,
    enabled
  );

  if (!enabled) {
    return availableSavedColumns;
  }

  const legacyDefaultColumns = getAvailableSubmittalColumns(
    normalizedDefaultColumns,
    false
  );
  const hasSavedMaterialReleaseColumn = normalizedSavedColumns.some((column) =>
    MATERIAL_RELEASE_COLUMNS.includes(column)
  );

  if (
    !hasSavedMaterialReleaseColumn &&
    haveSameColumnSet(availableSavedColumns, legacyDefaultColumns)
  ) {
    return getDefaultSubmittalColumns(normalizedDefaultColumns, true);
  }

  return availableSavedColumns;
};

export const getAvailableSubmittalFilters = (filters = {}, enabled = false) => {
  if (enabled) {
    return filters;
  }

  return Object.fromEntries(
    Object.entries(filters).filter(
      ([key]) => !MATERIAL_RELEASE_FILTER_FIELDS.includes(key)
    )
  );
};

export const getAvailableSubmittalSortField = (sortField, enabled = false) => {
  if (enabled || !MATERIAL_RELEASE_COLUMNS.includes(sortField)) {
    return sortField;
  }

  return null;
};

export const parseReminderFormValues = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }

  const parsed = values
    .map((value) => {
      if (typeof value === "number") {
        return value;
      }
      const trimmed = String(value).trim();
      if (!trimmed) {
        return NaN;
      }
      return Number.parseInt(trimmed, 10);
    })
    .filter((value) => Number.isFinite(value));

  return [...new Set(parsed)].sort((a, b) => a - b);
};

